// What a failed user delete (DELETE customers/delete/ or employees/delete/)
// shows the person who asked for it.
//
// The admin's 409 for a record that still owns live Microgrids (code
// "owns_microgrids") is the one reply whose own text is shown: its `error`
// sentence and the Microgrids' names, never their ids. Every other reply shows
// the webapp's own sentence for its status, so no other server text reaches
// the page. A server string holding an "@" (an email) is never returned, on
// any path, and `body.message` is never read.
//
// Pure: no React, fetch or toast. The page reads the body with
// response.json().catch(() => null) and passes it here.

export const OWNS_MICROGRIDS = "owns_microgrids";

// The pages' own success text. The success reply's body carries the email,
// so it is never shown.
export const DELETE_SUCCESS = Object.freeze({
  customer: "Customer deleted.",
  employee: "Employee deleted.",
});

// The admin's sentence (OWNS_MICROGRIDS_MESSAGE), used when the 409's own
// `error` is missing or can't be shown.
const OWNS_MICROGRIDS_DEFAULT =
  "This user's Customer still owns Microgrids. Delete or transfer them first.";

const TEXT = Object.freeze({
  network: "Couldn't reach the server. Check your connection and try again.",
  conflict: "This user can't be deleted right now. Refresh and try again.",
  bad_request:
    "The delete request was incomplete. Refresh the page and try again.",
  unauthorized: "Your session has expired. Sign in again, then retry.",
  forbidden: "You don't have permission to delete this user.",
  not_found:
    "This user wasn't found. They may already be deleted; refresh the list.",
  server:
    "The delete failed on the server. Try again; if it keeps failing, contact support.",
});

const STATUS_KINDS = Object.freeze({
  400: "bad_request",
  401: "unauthorized",
  403: "forbidden",
  404: "not_found",
});

// A server string the page may show: non-empty and holding no "@".
function isShowable(value) {
  return typeof value === "string" && value.trim() !== "" && !value.includes("@");
}

// Only a JSON object counts as a body; null, an HTML string, a number or an
// array leaves the status to decide.
function asObject(body) {
  return body !== null && typeof body === "object" && !Array.isArray(body)
    ? body
    : null;
}

function failure(kind, message) {
  return { kind, message, microgrids: [], count: 0 };
}

function ownsMicrogrids(body) {
  const entries = Array.isArray(body.microgrids) ? body.microgrids : [];
  const microgrids = entries
    .filter((entry) => asObject(entry) !== null && isShowable(entry.name))
    .map((entry) => entry.name);
  const count =
    Number.isInteger(body.count) && body.count >= microgrids.length
      ? body.count
      : microgrids.length;
  return {
    kind: OWNS_MICROGRIDS,
    message: isShowable(body.error) ? body.error : OWNS_MICROGRIDS_DEFAULT,
    microgrids,
    count,
  };
}

// { status, body, networkError } -> { kind, message, microgrids, count }.
// `microgrids` holds names only, in the body's order (the admin sorts them).
export function parseDeleteUserError({ status, body, networkError } = {}) {
  if (networkError === true) return failure("network", TEXT.network);
  if (!Number.isInteger(status)) return failure("unknown", "The delete failed.");

  if (status === 409) {
    const reply = asObject(body);
    return reply !== null && reply.code === OWNS_MICROGRIDS
      ? ownsMicrogrids(reply)
      : failure("conflict", TEXT.conflict);
  }
  const kind = STATUS_KINDS[status];
  if (kind) return failure(kind, TEXT[kind]);
  if (status >= 500 && status <= 599) return failure("server", TEXT.server);
  return failure("unknown", `The delete failed (status ${status}).`);
}

// The toast's text: the message, then for the 409 the first `maxNames` names
// with the total and how many more.
export function formatDeleteUserError(parsed, { maxNames = 5 } = {}) {
  if (parsed.kind !== OWNS_MICROGRIDS || parsed.microgrids.length === 0) {
    return parsed.message;
  }
  const shown = parsed.microgrids.slice(0, maxNames);
  const more = parsed.count - shown.length;
  const names =
    more > 0 ? `${shown.join(", ")} and ${more} more` : shown.join(", ");
  return `${parsed.message} Microgrids (${parsed.count}): ${names}.`;
}
